import type { FC, ReactNode } from 'react';
import { useLocation } from 'react-router';
import type { LoadedAppShellData } from 'src/components/layout/app-shell/use-app-shell-data';
import { Link } from 'src/components/navigation/link';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { flattenChildren } from 'src/utils/flatten-children';

type SettingsSidebarItemsProps = {
  appShellData: LoadedAppShellData;
};

export const SettingsSidebarItems: FC<SettingsSidebarItemsProps> = ({
  appShellData: {
    authorizationService,
    organisationService,
    capacityManagementService,
    regularFcfsService,
    directFcfsService,
    hydrogenFcfsService,
    assignmentService,
    remitReportingService,
    surrenderServiceRoot,
  },
}) => {
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const { pathname, hash } = useLocation();
  return (
    <div className="grid w-[20rem] gap-2xl">
      <SettingsSidebarSection title="Personal Settings">
        <Link to="/settings/profile">Profile</Link>

        <Link to="/settings/notifications">Notification Settings</Link>

        {monolithUser?.isShipper && (
          <Link
            isActive={
              pathname === '/settings/platform/' &&
              hash === '#/user-settings/general'
            }
            to="/settings/platform/#/user-settings/general"
          >
            View
          </Link>
        )}

        {monolithUser?.isShipper && (
          <Link
            isActive={
              pathname === '/settings/platform/' &&
              hash === '#/user-settings/dashboard'
            }
            to="/settings/platform/#/user-settings/dashboard"
          >
            Dashboard
          </Link>
        )}

        {monolithUser?.isShipper && (
          <Link
            isActive={
              pathname === '/settings/platform/' &&
              hash === '#/user-settings/network-points'
            }
            to="/settings/platform/#/user-settings/network-points"
          >
            Network Points
          </Link>
        )}

        {monolithUser?.isShipper && (
          <Link to="/settings/integration-client-auth">
            Booking Authorisation
          </Link>
        )}
      </SettingsSidebarSection>

      <SettingsSidebarSection title="Company Settings">
        {organisationService?.response.data._links.getUserDetails && (
          <Link
            to="/settings/organisation/users"
            data-testid="user-management-link"
          >
            User Management
          </Link>
        )}

        {authorizationService?.response.data._links.permissionGroups && (
          <Link to="/settings/permission-groups">Permission Groups</Link>
        )}

        {organisationService?.response.data._links.getMyOrganisationDetails && (
          <Link to="/settings/organisation/company-information">
            Company Information
          </Link>
        )}

        {capacityManagementService?._links?.settingsInvoicing && (
          <Link to="/settings/organisation/invoices">Invoice Settings</Link>
        )}

        {organisationService?.response.data._links.contacts && (
          <Link to="/settings/organisation/contacts">Contacts</Link>
        )}

        {(monolithUser?.role === 'SHIPPER_ADMIN' ||
          remitReportingService?._links?.['secondary-settings-details']) && (
          <Link to="/settings/premium-services">Premium Services</Link>
        )}

        {monolithUser?.isSso && (
          <Link
            to="/settings/platform/#/storage-settings/premium-services"
            isActive={
              pathname === '/settings/platform/' &&
              hash === '#/storage-settings/premium-services'
            }
          >
            Premium Services
          </Link>
        )}
      </SettingsSidebarSection>

      <SettingsSidebarSection title="Transport Settings">
        {monolithUser?.isShipper && (
          <Link
            isActive={
              pathname === '/settings/platform/' &&
              (hash === '#/user-settings/secondary-trading' ||
                hash === '#/user-settings/new-trader-list')
            }
            to="/settings/platform/#/user-settings/secondary-trading"
          >
            Secondary Trading
          </Link>
        )}

        {monolithUser?.isTso && (
          <Link to="/settings/general-transport-settings">General</Link>
        )}

        {monolithUser?.isTso && <Link to="/settings/auctions">Auctions</Link>}

        {monolithUser?.isTso && (
          <Link
            to="/settings/platform/#/operator-settings/secondary-trading"
            isActive={
              pathname === '/settings/platform/' &&
              hash === '#/operator-settings/secondary-trading'
            }
          >
            Secondary Trading
          </Link>
        )}

        {monolithUser?.isTso ? (
          surrenderServiceRoot?._links.isSurrenderForAllEnabled ? (
            <Link to="/settings/surrender">Surrender</Link>
          ) : (
            <Link
              to="/settings/platform/#/operator-settings/surrender"
              isActive={
                pathname === '/settings/platform/' &&
                hash === '#/operator-settings/surrender'
              }
            >
              Surrender
            </Link>
          )
        ) : null}

        {regularFcfsService?._links?.['operator-settings'] && (
          <Link to="/settings/regular-fcfs">Regular FCFS</Link>
        )}

        {directFcfsService?._links?.['read-configuration'] && (
          <Link to="/settings/direct-fcfs">Direct FCFS</Link>
        )}

        {hydrogenFcfsService?._links?.organisationFcfsSettings && (
          <Link to="/settings/hydrogen-fcfs">Hydrogen FCFS</Link>
        )}
      </SettingsSidebarSection>

      <SettingsSidebarSection title="Storage Settings">
        {monolithUser?.isSso && (
          <Link
            to="/settings/platform/#/storage-settings/general"
            isActive={
              pathname === '/settings/platform/' &&
              hash === '#/storage-settings/general'
            }
          >
            General
          </Link>
        )}
      </SettingsSidebarSection>

      <SettingsSidebarSection title="Reporting Settings">
        {monolithUser?.role === 'SHIPPER_ADMIN' && (
          <Link to="/settings/reporting/standard-reports">
            Standard Reports
          </Link>
        )}

        {(remitReportingService?._links?.['exclude-network-points'] ||
          remitReportingService?._links?.['primary-settings-details']) && (
          <Link to="/settings/remit-reporting">REMIT Reporting</Link>
        )}
      </SettingsSidebarSection>

      <SettingsSidebarSection title="Shipper Settings">
        {assignmentService?.response.data._links.assignmentSettings && (
          <Link to="/settings/assignment-settings">Assignment Settings</Link>
        )}

        {monolithUser?.isTso && (
          <Link
            to="/settings/platform/#/operator-settings/credit-limits"
            isActive={
              pathname === '/settings/platform/' &&
              hash === '#/operator-settings/credit-limits'
            }
          >
            Credit Limits
          </Link>
        )}
      </SettingsSidebarSection>
    </div>
  );
};

/**
 * Groups settings links under a title. Nothing will be rendered, if there are
 * no links (e.g. because all of them are hidden by permission checks).
 */
const SettingsSidebarSection: FC<{
  title: string;
  children: ReactNode;
}> = ({ title, children }) => {
  const items = flattenChildren(children);
  if (!items.length) return null;

  return (
    <div className="grid gap-sm">
      <p
        className={`
          text-body-sm font-label-semibold text-foreground-surface-subdued
        `}
      >
        {title}
      </p>

      <div className="grid gap-md">
        {items.map((item, index) => (
          <div
            key={index}
            className={`
              grid text-left
              last:border-b-0
            `}
          >
            {item}
          </div>
        ))}
      </div>
    </div>
  );
};
